import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { API_URL, formatPrice, COUNTRY_FLAGS, CURR_SYMBOLS } from './AdminShared';
import { DollarSign, CheckCircle, Clock, Users, TrendingUp, KeyRound, Eye, EyeOff } from 'lucide-react';

export const RevenueTab = () => {
  const [pinVerified, setPinVerified] = useState(false);
  const [pin, setPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [pinError, setPinError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [data, setData] = useState(null);
  const [loadingData, setLoadingData] = useState(false);

  const verifyPin = async () => {
    setVerifying(true);
    setPinError('');
    try {
      await axios.post(`${API_URL}/api/admin/revenue/verify-pin`, { pin });
      setPinVerified(true);
      fetchRevenue(pin);
    } catch (error) {
      setPinError(error.response?.data?.detail || 'Invalid PIN');
    } finally {
      setVerifying(false);
    }
  };

  const fetchRevenue = async (verifiedPin) => {
    setLoadingData(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/revenue/analytics`, { pin: verifiedPin });
      setData(res.data);
    } catch (error) {
      toast.error('Failed to load revenue data');
      setPinVerified(false);
    } finally {
      setLoadingData(false);
    }
  };

  const handlePinKeyDown = (e) => {
    if (e.key === 'Enter') verifyPin();
  };

  // PIN Gate
  if (!pinVerified) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="luxury-card p-10 max-w-sm w-full text-center" data-testid="revenue-pin-gate">
          <div className="w-16 h-16 bg-[#0F172A] flex items-center justify-center mx-auto mb-6">
            <KeyRound className="h-7 w-7 text-[#E07A3A]" />
          </div>
          <h3 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-2">Revenue Access</h3>
          <p className="text-slate-500 text-sm mb-6">Enter your secure PIN to view revenue analytics</p>
          <div className="relative mb-4">
            <input
              type={showPin ? 'text' : 'password'}
              value={pin}
              onChange={e => { setPin(e.target.value); setPinError(''); }}
              onKeyDown={handlePinKeyDown}
              placeholder="Enter PIN"
              maxLength={10}
              className="w-full border border-slate-200 px-4 py-3 pr-12 text-center text-xl tracking-[0.5em] text-[#0F172A] focus:border-[#E07A3A] focus:outline-none font-mono"
              autoFocus
              data-testid="revenue-pin-input"
            />
            <button
              type="button"
              onClick={() => setShowPin((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#E07A3A] transition-colors"
              aria-label={showPin ? 'Hide PIN' : 'Show PIN'}
              data-testid="revenue-pin-toggle"
              tabIndex={-1}
            >
              {showPin ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
          {pinError && <p className="text-red-500 text-sm mb-3" data-testid="revenue-pin-error">{pinError}</p>}
          <Button
            onClick={verifyPin}
            disabled={verifying || !pin}
            className="w-full bg-[#E07A3A] hover:bg-[#C4652E] text-white py-6 text-sm tracking-widest uppercase font-semibold"
            data-testid="revenue-pin-submit"
          >
            {verifying ? 'Verifying...' : 'Unlock Revenue'}
          </Button>
        </div>
      </div>
    );
  }

  if (loadingData || !data) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#E07A3A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const ov = data.overview;
  const maxMonthlyRevenue = Math.max(...(data.monthly_trend || []).map(m => m.revenue), 1);

  return (
    <div className="space-y-8" data-testid="revenue-dashboard">
      {/* Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Revenue', value: `£${formatPrice(ov.total_revenue)}`, icon: <DollarSign className="h-5 w-5" />, accent: true },
          { label: 'Completed Txns', value: ov.completed_transactions, icon: <CheckCircle className="h-5 w-5" /> },
          { label: 'Pending Txns', value: ov.pending_transactions, icon: <Clock className="h-5 w-5" />, alert: ov.pending_transactions > 0 },
          { label: 'Total Users', value: ov.total_users, icon: <Users className="h-5 w-5" /> },
        ].map((stat) => (
          <div key={stat.label} className={`luxury-card p-6 ${stat.accent ? 'bg-[#0F172A]' : ''} ${stat.alert ? 'border-amber-300' : ''}`} data-testid={`rev-stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className={`mb-3 ${stat.accent ? 'text-[#E07A3A]' : 'text-[#E07A3A]'}`}>{stat.icon}</div>
            <p className={`text-3xl font-['Playfair_Display'] ${stat.accent ? 'text-white' : 'text-[#1A1A1A]'}`}>{stat.value}</p>
            <p className={`text-sm mt-1 ${stat.accent ? 'text-white/50' : 'text-slate-500'}`}>{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Users by Role */}
      <div className="luxury-card p-6">
        <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-4">Users by Role</h3>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {Object.entries(ov.users_by_role || {}).map(([role, count]) => (
            <div key={role} className="bg-slate-50 p-4 text-center">
              <p className="text-2xl font-['Playfair_Display'] text-[#E07A3A]">{count}</p>
              <p className="text-xs text-slate-500 uppercase tracking-wider font-medium mt-1">{role}s</p>
            </div>
          ))}
        </div>
      </div>

      {/* Revenue by Country */}
      <div className="luxury-card p-6">
        <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-4">Revenue by Country</h3>
        {Object.keys(data.by_country || {}).length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {Object.entries(data.by_country).map(([country, info]) => {
              const sym = CURR_SYMBOLS[info.currency] || info.currency.toUpperCase() + ' ';
              return (
                <div key={country} className="bg-[#0F172A] p-5" data-testid={`rev-country-${country.toLowerCase()}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-lg">{COUNTRY_FLAGS[country] || COUNTRY_FLAGS.Other}</span>
                    <span className="text-white font-semibold text-sm tracking-wider uppercase">{country}</span>
                  </div>
                  <p className="text-white font-['Playfair_Display'] text-2xl">{sym}{formatPrice(info.revenue)}</p>
                  <p className="text-white/40 text-xs mt-1">{info.count} transaction{info.count !== 1 ? 's' : ''}</p>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-slate-400 text-sm">No completed transactions yet</p>
        )}
      </div>

      {/* Revenue by Fee Type */}
      <div className="luxury-card p-6">
        <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-4">Revenue by Fee Type</h3>
        {Object.keys(data.by_fee_type || {}).length > 0 ? (
          <div className="space-y-3">
            {Object.entries(data.by_fee_type).map(([type, info]) => {
              const pct = ov.total_revenue > 0 ? (info.revenue / ov.total_revenue) * 100 : 0;
              return (
                <div key={type} data-testid={`rev-fee-${type.toLowerCase().replace(/\s+/g, '-')}`}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-[#1A1A1A] font-medium">{type}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-slate-400">{info.count} txn{info.count !== 1 ? 's' : ''}</span>
                      <span className="text-sm text-[#E07A3A] font-semibold font-['Playfair_Display']">£{formatPrice(info.revenue)}</span>
                    </div>
                  </div>
                  <div className="w-full bg-slate-100 h-2">
                    <div className="bg-[#E07A3A] h-2 transition-all duration-500" style={{ width: `${Math.max(pct, 1)}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-slate-400 text-sm">No completed transactions yet</p>
        )}
      </div>

      {/* Monthly Trend */}
      <div className="luxury-card p-6">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="h-5 w-5 text-[#E07A3A]" />
          <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A]">Monthly Trend</h3>
        </div>
        {(data.monthly_trend || []).length > 0 ? (
          <div className="flex items-end gap-2 h-48" data-testid="revenue-monthly-chart">
            {data.monthly_trend.map((m) => {
              const heightPct = maxMonthlyRevenue > 0 ? (m.revenue / maxMonthlyRevenue) * 100 : 0;
              const monthLabel = new Date(m.month + '-01').toLocaleDateString('en', { month: 'short', year: '2-digit' });
              return (
                <div key={m.month} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                  <span className="text-xs text-[#E07A3A] font-semibold">£{formatPrice(m.revenue)}</span>
                  <div className="w-full bg-slate-100 rounded-t relative" style={{ height: '140px' }}>
                    <div
                      className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#E07A3A] to-[#E07A3A]/70 transition-all duration-700 rounded-t"
                      style={{ height: `${Math.max(heightPct, 3)}%` }}
                    />
                  </div>
                  <span className="text-xs text-slate-400 font-medium truncate w-full text-center">{monthLabel}</span>
                  <span className="text-xs text-slate-300">{m.count} txn</span>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-slate-400 text-sm">No monthly data available yet</p>
        )}
      </div>

      {/* Professional Revenue */}
      {(data.professionals || []).length > 0 && (
        <div className="luxury-card p-6">
          <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-4">Professional Revenue</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="revenue-pro-table">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-2 text-xs text-slate-500 font-medium uppercase tracking-wider">Name</th>
                  <th className="text-left py-3 px-2 text-xs text-slate-500 font-medium uppercase tracking-wider">Type</th>
                  <th className="text-left py-3 px-2 text-xs text-slate-500 font-medium uppercase tracking-wider">Country</th>
                  <th className="text-right py-3 px-2 text-xs text-slate-500 font-medium uppercase tracking-wider">Access</th>
                  <th className="text-right py-3 px-2 text-xs text-slate-500 font-medium uppercase tracking-wider">Leads</th>
                  <th className="text-right py-3 px-2 text-xs text-slate-500 font-medium uppercase tracking-wider">Completed</th>
                  <th className="text-right py-3 px-2 text-xs text-slate-500 font-medium uppercase tracking-wider">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {data.professionals.map((pro, i) => (
                  <tr key={pro.email || i} className="border-b border-slate-100 hover:bg-slate-50/50">
                    <td className="py-3 px-2">
                      <p className="text-[#1A1A1A] font-medium">{pro.name}</p>
                      <p className="text-xs text-slate-400">{pro.email}</p>
                    </td>
                    <td className="py-3 px-2 text-slate-600 capitalize">{pro.type?.replace('_', ' ') || '-'}</td>
                    <td className="py-3 px-2">{COUNTRY_FLAGS[pro.country] || ''} {pro.country}</td>
                    <td className="py-3 px-2 text-right text-slate-600">{pro.access_purchases}</td>
                    <td className="py-3 px-2 text-right text-slate-600">{pro.total_leads}</td>
                    <td className="py-3 px-2 text-right text-slate-600">{pro.completed_leads}</td>
                    <td className="py-3 px-2 text-right text-[#E07A3A] font-semibold font-['Playfair_Display']">£{formatPrice(pro.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
